/**
 * **A port of `legacy/frontend/src/screens/ProgramScreen.tsx`, whole but for its `:3-11`.** F-024
 * T5: «Программа», the programme of one service — its numbers, their order, who sings them — pushed
 * from a service's card on «Расписание» and «Что прошло» by `SessionsScreen` (T11), and from the
 * hub's tile by `ChoirScreen`'s `program` arm (T14). Mounted by neither until those tasks land.
 *
 * Their `:3-11` are one import mixing the values `ApiError` and `api` with the types `ChoirSongRow`,
 * `MemberRow`, `Program`, `ProgramItemKind` and `ProgramItemRow` from `../api`. The types are server
 * shapes, so here they come from `@contracts/index` (ADR-0011), and the nine lines become two. Their
 * `:1-2` and their `:12` to the end are theirs byte for byte, and `scripts/ported-partial.txt` holds
 * both regions.
 *
 * Carried as it is, every one `KEEP` (human, 2026-09-30): a closed service's programme offering
 * every write, the FAB included, for the server to refuse (`FND-1563`); «Кто поёт» leaking its names
 * into «Новый номер» (`FND-1564`); a paused singer who cannot be taken off a number (`FND-1565`); one
 * refusal slot, drawn in two places and outliving its sheet (`FND-1567`); «Новый номер» keeping its
 * draft, a choir song's title under another kind included (`FND-1568`); and an order that can only
 * be set with a pointer (`FND-1569`).
 *
 * The load's refusal text (their `:166-169`) is ported as written and read by nothing: `Failed`
 * takes no text and `refusal()` is never called with `'screen'` — held by review, `SongsScreen`'s
 * shape.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import type { ChoirSongRow, MemberRow, Program, ProgramItemKind, ProgramItemRow } from '@contracts/index';
import { ApiError, api } from '../api';
import { formatMonthYear, formatTime, formatWeekdayDate } from '../format';
import { DotsIcon, GripIcon, PeopleIcon, PlusIcon } from '../icons';
import useLeaving from '../leaving';
import { useWake } from '../liveness';
import { useBackKey } from '../backKey';
import Ask from './Ask';
import { subLine } from './program';
import Sheet from './Sheet';
import Unfold from './Unfold';
import { Empty, Failed, Settling, Skeleton } from './States';

/**
 * The four kinds of number, in the order the sheet offers them.
 *
 * A word and not a chip on the row itself: four types down six rows would be
 * six coloured plates on a screen that is read top to bottom, and the type is a
 * detail about a number rather than a status of it.
 */
const KINDS: [ProgramItemKind, string][] = [
  ['verse', 'Стих'],
  ['song', 'Песня'],
  ['sermon', 'Проповедь'],
  ['testimony', 'Свидетельство'],
];

/** «вс 31.08 · 10:00 · 31 да · 2 нет» — the service the list belongs to. */
function caption(program: Program): string {
  const parts = [formatWeekdayDate(program.start), formatTime(program.start)];
  if (program.counts.going > 0) parts.push(`${program.counts.going} да`);
  if (program.counts.not_going > 0) parts.push(`${program.counts.not_going} нет`);
  return parts.join(' · ');
}


/** Which row is under the finger, and the arithmetic that follows it. */
type Drag = {
  itemId: number;
  /** Where the finger went down, moved on by a row every time one is passed. */
  startY: number;
  /** How far the row has travelled from where it now belongs. */
  dy: number;
  /**
   * Every row's own height, measured when the finger went down and reordered
   * with the list.
   *
   * Not one number for all of them, which is what a list of equal rows would
   * allow: a number's caption wraps, so a row here is anywhere from 56 to 89
   * pixels tall. Against a single average the step fires when the finger has
   * travelled half of *that*, which on this list is a third of the way past a
   * tall neighbour — the list jumping before the row has got there. The rule
   * the handoff states is «as it passes the middle of its neighbour», and the
   * neighbour is the only thing that can answer it.
   */
  heights: number[];
  /** The order before anybody touched it, for a save the server refuses. */
  was: number[];
};

/** One row and the hairline under it, which the row's own height leaves out. */
function heightsOf(list: HTMLElement): number[] {
  return [...list.children].map((row) => (row as HTMLElement).offsetHeight + 1);
}

/**
 * The programme of one service: what the choir has signed up to do at it.
 *
 * Anybody signs a number up and nothing approves it — the owner's answer, and
 * it is what makes the screen this small: no review state, no queue, no
 * notification. What is *not* anybody's is the order, the choir's own songs and
 * who sings somebody else's number: those three are the programme keeper's, and
 * the regent's, who never loses a function he has handed on.
 *
 * Pushed from the service's own card in the schedule, and from the tile on
 * «Хор». It used to be told which of the two, to name it in its own back bar;
 * the client's arrow is unlabelled and the screen no longer needs to know.
 */
export default function ProgramScreen({
  sessionId,
  onBack,
}: {
  sessionId: string;
  onBack: () => void;
}) {
  const [program, setProgram] = useState<Program | null>(null);
  /**
   * The order on screen, which the drag moves before the server has agreed.
   * Held apart from `program` for that one reason: everything else about a
   * number comes from the server and this one thing is optimistic.
   */
  const [items, setItems] = useState<ProgramItemRow[]>([]);
  /* A number that has been taken out stays on the screen for the length of its
     own closing. The list is drawn from this; the count in the label is not,
     because the count is what the programme *is* and this is what is on screen
     for a fifth of a second longer. */
  const standing = useLeaving(items, (one) => one.item_id);
  /** The roster, for the two pickers of who sings. */
  const [members, setMembers] = useState<MemberRow[]>([]);
  /** The repertoire, offered in the sheet to whoever keeps the programme. */
  const [songs, setSongs] = useState<ChoirSongRow[]>([]);
  /** The refusal and where it belongs: `screen`, `form`, `order`, or an id. */
  const [failure, setFailure] = useState<{ at: string; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [kind, setKind] = useState<ProgramItemKind>('verse');
  const [title, setTitle] = useState('');
  /** Which choir song the new number is, if it is one. */
  const [songId, setSongId] = useState<number | null>(null);
  /** Who sings it, in the sheet that is open — the new number's or one row's. */
  const [singers, setSingers] = useState<number[]>([]);
  /** Whose «Кто поёт» is open, by item id. */
  const [naming, setNaming] = useState<ProgramItemRow | null>(null);
  /** Whose «…» is unfolded, by item id. */
  const [menuFor, setMenuFor] = useState<number | null>(null);
  /** Which number is being asked about, by item id. */
  const [removing, setRemoving] = useState<number | null>(null);
  const [drag, setDrag] = useState<Drag | null>(null);

  /**
   * The gesture's own copy of both, read by listeners that are attached once.
   *
   * `dragging` is written before `setDrag` rather than during the render that
   * follows it, and that is not an optimisation: `pointerdown` and the first
   * `pointermove` can land in one task, and a ref assigned on render is still
   * null when they do — the row then follows nothing at all.
   */
  const dragging = useRef<Drag | null>(null);
  const ordered = useRef<ProgramItemRow[]>(items);
  ordered.current = items;

  /** One place that moves the gesture, so the ref and the render never differ. */
  const hold = useCallback((next: Drag | null) => {
    dragging.current = next;
    setDrag(next);
  }, []);

  /** The same for the order, which the drag moves before any render. */
  const reorder = useCallback((next: ProgramItemRow[]) => {
    ordered.current = next;
    setItems(next);
  }, []);

  const land = useCallback(
    (answer: Program) => {
      setProgram(answer);
      reorder(answer.items);
    },
    [reorder],
  );

  const load = useCallback(async () => {
    try {
      land(await api.program(sessionId));
      setFailure(null);
    } catch (error) {
      setFailure({
        at: 'screen',
        text: error instanceof ApiError ? error.message : 'Не удалось загрузить программу',
      });
    }
  }, [sessionId, land]);

  useEffect(() => {
    void load();
  }, [load]);

  useBackKey(onBack);

  // The roster is the two singer pickers; without it the sheet offers nobody.
  useEffect(() => {
    async function roster(): Promise<void> {
      try {
        setMembers((await api.choirMembers()).filter((row) => row.standing === 'active'));
      } catch {
        // The pickers stand empty and the rest of the screen is unaffected:
        // signing a number up has never needed a list of names.
      }
    }
    void roster();
  }, []);

  // Only for whoever may put one in. A singer who cannot has nothing to do with
  // the repertoire on this screen, and «Песни хора» is where it is read.
  useEffect(() => {
    if (program?.can_order !== true) return;
    async function repertoire(): Promise<void> {
      try {
        setSongs(await api.choirSongs());
      } catch {
        // The block of capsules is simply not drawn.
      }
    }
    void repertoire();
  }, [program?.can_order]);

  // Somebody else signing up while this phone was in a pocket is exactly what
  // the person coming back to this screen wants to see — unless a finger is on
  // a row, in which case the reload would take the list out from under it.
  useWake(() => {
    if (dragging.current !== null) return;
    void load();
  });

  function refusal(at: string) {
    return failure !== null && failure.at === at ? <p className="failure">{failure.text}</p> : null;
  }

  async function act(action: () => Promise<Program>, at: string): Promise<boolean> {
    setBusy(true);
    setFailure(null);
    try {
      land(await action());
      return true;
    } catch (error) {
      setFailure({
        at,
        text: error instanceof ApiError ? error.message : 'Не получилось, попробуй ещё раз',
      });
      return false;
    } finally {
      setBusy(false);
    }
  }

  /**
   * The gesture, and it is four events: down on the handle, move on the window,
   * up anywhere.
   *
   * The order changes as the row passes the middle of a neighbour rather than
   * when the finger lifts — that is what makes the list answer the hand instead
   * of reporting afterwards. A step is `round(offset / rowHeight)`, and once a
   * step has been taken the origin moves with it, so the offset the row is
   * drawn at stays small and the next step is measured from where it now is.
   *
   * There is no motion in any of this. The app has one animation and it is the
   * code folding; the row follows the finger and its neighbours change places
   * in the same frame.
   */
  function grab(event: React.PointerEvent<HTMLButtonElement>, itemId: number): void {
    event.preventDefault();
    const handle = event.currentTarget;
    handle.setPointerCapture?.(event.pointerId);
    const list = handle.closest('ul');
    hold({
      itemId,
      startY: event.clientY,
      dy: 0,
      heights: list === null ? [] : heightsOf(list),
      was: ordered.current.map((one) => one.item_id),
    });
  }

  useEffect(() => {
    function move(event: PointerEvent): void {
      const held = dragging.current;
      if (held === null) return;
      const next = ordered.current.slice();
      const heights = held.heights.slice();
      let at = next.findIndex((one) => one.item_id === held.itemId);
      if (at === -1) return;

      let dy = event.clientY - held.startY;
      let startY = held.startY;
      let moved = false;

      // One neighbour at a time, and the neighbour's own height is what says
      // whether it has been passed. After a swap the origin moves with the row,
      // so the offset it is drawn at stays small and the next step is measured
      // from where it now is.
      const swap = (a: number, b: number): void => {
        [next[a], next[b]] = [next[b], next[a]];
        [heights[a], heights[b]] = [heights[b], heights[a]];
      };
      while (at < next.length - 1 && dy > heights[at + 1] / 2) {
        const passed = heights[at + 1];
        swap(at, at + 1);
        dy -= passed;
        startY += passed;
        at += 1;
        moved = true;
      }
      while (at > 0 && dy < -heights[at - 1] / 2) {
        const passed = heights[at - 1];
        swap(at, at - 1);
        dy += passed;
        startY -= passed;
        at -= 1;
        moved = true;
      }

      if (moved) reorder(next);
      hold({ ...held, startY, dy, heights });
    }

    function drop(): void {
      const held = dragging.current;
      if (held === null) return;
      hold(null);
      const now = ordered.current.map((one) => one.item_id);
      if (now.join() === held.was.join()) return;
      // Optimistic: the list is already where the finger left it, and a refusal
      // puts back the order the drag started from rather than leaving the screen
      // claiming something the server does not hold.
      void act(() => api.setProgramOrder(sessionId, now), 'order').then((ok) => {
        if (ok) return;
        const by = new Map(ordered.current.map((one) => [one.item_id, one]));
        reorder(held.was.map((id) => by.get(id)).filter((one) => one !== undefined));
      });
    }

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', drop);
    window.addEventListener('pointercancel', drop);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', drop);
      window.removeEventListener('pointercancel', drop);
    };
    // `act` is read through refs and props that do not change for the life of
    // this screen; the listeners are attached once so that a render mid-drag
    // does not drop the gesture.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, hold, reorder]);

  const doomed = items.find((row) => row.item_id === removing) ?? null;
  const trimmed = title.trim();
  const canOrder = program?.can_order === true;

  /** One name in a picker, as a capsule. The pair of states is the answer's. */
  function singerChip(member: MemberRow) {
    const picked = singers.includes(member.member_id);
    return (
      <button
        key={member.member_id}
        className={
          picked ? 'answer answer--input answer--picked' : 'answer answer--input answer--blank'
        }
        aria-pressed={picked}
        onClick={() =>
          setSingers((current) =>
            picked
              ? current.filter((id) => id !== member.member_id)
              : [...current, member.member_id],
          )
        }
      >
        {member.display_name}
      </button>
    );
  }

  function row(item: ProgramItemRow) {
    const open = menuFor === item.item_id;
    const lifted = drag?.itemId === item.item_id;
    return (
      <li
        key={item.item_id}
        className={
          [
            'live-row',
            'live-row--stack',
            open ? 'live-row--menu' : null,
            lifted ? 'live-row--lifted' : null,
            // «Убрать номер» was answered and the server has already sent the
            // programme back without it. The row closes rather than being cut
            // out from under the numbers below it.
            standing.leaving(item) ? 'row--going' : null,
          ]
            .filter((name) => name !== null)
            .join(' ')
        }
        style={lifted ? { transform: `translateY(${drag.dy}px)` } : undefined}
      >
        <span className="live-head">
          <span className="program-n">{item.position}</span>
          <span className="live-body">
            <span className="program-title">
              <span className="live-name">{item.title}</span>
              {/* The whole choir sings this one, which is why nobody is listed
                  under it. A mark and not a control, so it is the read form of
                  `.answer` in the accent, at the face and the size every other
                  mark in this app is set in. */}
              {item.is_choir && (
                <span className="answer answer--mark" data-tone="accent">
                  хор
                </span>
              )}
            </span>
            {/* Two lines rather than one clipped: the names are what this line
                exists to carry, and «поют Ирина Ковалец, Настя Пух, Олег Кот»
                does not fit 375px beside two icons. */}
            <span className="live-sub live-sub--wrap">{subLine(item)}</span>
          </span>

          {/* Only on a personal song, and only for the person who wrote it down
              or whoever keeps the programme. The server decides both — see
              `can_set_singers` — so the screen never offers a tap that would be
              refused. */}
          {item.can_set_singers && (
            <button
              className="row-icon"
              data-press="fade"
              aria-label={`Кто поёт «${item.title}»`}
              onClick={() => {
                setNaming(item);
                setSingers(item.singers.map((one) => one.member_id));
              }}
            >
              <PeopleIcon size={20} />
            </button>
          )}

          {/* Only on a number somebody may take away: on everybody else's row
              there is nothing behind the switch, and a control that opens an
              empty panel is worse than no control. */}
          {item.can_remove && (
            <button
              className="live-dots"
              data-press="none"
              aria-label={`Что можно сделать с номером «${item.title}»`}
              aria-expanded={open}
              onClick={() => setMenuFor(open ? null : item.item_id)}
            >
              <DotsIcon size={20} />
            </button>
          )}

          {/* The handle, and it is the keeper's alone: a list anybody could
              reorder is a list nobody is responsible for. */}
          {canOrder && (
            <button
              className="row-icon row-icon--grip"
              data-press="none"
              aria-label={`Перетащить «${item.title}»`}
              onPointerDown={(event) => grab(event, item.item_id)}
            >
              <GripIcon size={20} />
            </button>
          )}
        </span>

        <Unfold open={open}>
          <div className="menu">
            <button
              className="menu-action menu-action--danger"
              disabled={busy}
              onClick={() => setRemoving(item.item_id)}
            >
              Убрать номер
            </button>
          </div>
        </Unfold>

        {refusal(`item:${item.item_id}`)}
      </li>
    );
  }

  /**
   * Who keeps the programme, said to whoever is reading it.
   *
   * Three states and not two, because a regent who has handed the programme on
   * still holds it himself — the owner's rule of 2026-08-24. A line that told
   * him somebody else sets the order would be the screen contradicting the
   * handle it is drawing for him.
   *
   * The holder is named and never referred to by a pronoun: «ставит он» beside
   * a woman's name is the trap this file's own rule about participles exists to
   * avoid, and «ведущий программы» is a role the way «регент» is.
   */
  function holderLine(answer: Program): string {
    const who = answer.holder_name ?? 'регент';
    if (answer.holder_is_me) {
      return 'Программу ведёшь ты. Тяни номер за ручку справа — порядок сохраняется сразу.';
    }
    if (answer.can_order) {
      return `Программу ведёт ${who}. Порядок можешь менять и ты — тяни номер за ручку справа.`;
    }
    return `Программу ведёт ${who}. Порядок номеров ставит ведущий программы.`;
  }

  return (
    <>
      <div className="screen-head">
        <h1 className="screen-title">{program?.title ?? 'Участие'}</h1>
        {program !== null && <p className="screen-caption">{caption(program)}</p>}
      </div>

      {program !== null && <p className="hint">{holderLine(program)}</p>}

      {/* One or the other, and neither once the programme is on screen: a
          refused reload leaves the numbers standing. */}
      {program === null && failure === null && <Skeleton />}
      {program === null && failure?.at === 'screen' && (
        <Failed subject="program" onRetry={() => void load()} />
      )}

      {program !== null && (
        <Settling>
          <div className="block">
            <span className="label">
              Программа · <span className="section-count">{items.length}</span>
            </span>
            {/* What is drawn decides, not what is left: the last number taken
                out is still closing, and «Номеров пока нет» arriving over it
                would be two answers to one tap. */}
            {standing.rows.length === 0 ? (
              <Empty
                fact="Номеров пока нет"
                note="Записаться может любой — номер закрепится за тобой."
              />
            ) : (
              <ul className="live">{standing.rows.map(row)}</ul>
            )}
            {/* A refused save is read where the drag happened, not under the tab
                bar: this list is the thing that moved. */}
            {refusal('order')}
            {/* Only what the line under the title does not already say. That one
                names who keeps the order and what to do about it; this one is the
                other half of the feature — nothing approves a sign-up. Both
                sentences carried the order at first, and two lines on one screen
                ending in the same words is the app repeating itself. */}
            <p className="hint">Записаться может любой, одобрения не нужно.</p>
          </div>
        </Settling>
      )}

      {/* The same capsule the schedule uses, in the same place: one action with
          one shape, wherever a list is the screen. */}
      {program !== null && (
        <button className="fab" onClick={() => setAdding(true)}>
          <span className="fab-word">Добавить номер</span>
          <PlusIcon size={14} cropped />
        </button>
      )}

      {/* A sheet, because this is filled in rather than looked at — the
          container rule of 2026-08-21. Four types in a 2×2 and not a `.seg`:
          four Russian words do not fit one 375px row. */}
      <Sheet
        open={adding}
        label="Новый номер"
        onClose={() => setAdding(false)}
        foot={
          <>
            {refusal('form')}
            <button
              className="btn btn--primary"
              disabled={busy || trimmed === ''}
              onClick={() =>
                void act(
                  () =>
                    api.addProgramItem(sessionId, kind, trimmed, {
                      choirSongId: songId,
                      singerIds: kind === 'song' && songId === null ? singers : [],
                    }),
                  'form',
                ).then((ok) => {
                  if (!ok) return;
                  setAdding(false);
                  setTitle('');
                  setKind('verse');
                  setSongId(null);
                  setSingers([]);
                })
              }
            >
              Записаться
            </button>
          </>
        }
      >
        <div className="field">
          <span>Что это будет</span>
          <div className="kinds">
            {KINDS.map(([value, label]) => (
              <button
                key={value}
                className={value === kind ? 'btn btn--selected' : 'btn btn--outline'}
                aria-pressed={value === kind}
                onClick={() => {
                  setKind(value);
                  setSongId(null);
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Only a song can come out of the repertoire, and only the keeper puts
            one in. Choosing one fills the title in and makes the number the
            whole choir's, which is why the picker of who sings it goes away
            underneath. */}
        {kind === 'song' && canOrder && songs.length > 0 && (
          <div className="field">
            <span>Из песен хора — тогда номер поёт весь хор</span>
            {/* A column and not a wrap of chips, because every row carries a
                second thing: when the choir last sang it. This is the moment
                that question is actually asked — «чтобы не петь одну и ту же
                через месяц» is asked while choosing, not while reading the
                repertoire — and the list arrives from the endpoint already
                ordered from forgotten to fresh, so «возьми сверху» works here
                too. */}
            <div className="chips chips--rows">
              {songs.map((song) => {
                const picked = songId === song.song_id;
                return (
                  <button
                    key={song.song_id}
                    className={
                      picked
                        ? 'answer answer--input answer--picked'
                        : 'answer answer--input answer--blank'
                    }
                    aria-pressed={picked}
                    onClick={() => {
                      setSongId(picked ? null : song.song_id);
                      setTitle(picked ? '' : song.title);
                      setSingers([]);
                    }}
                  >
                    <span className="song-pick-title">{song.title}</span>
                    <span className="song-when">
                      {song.last_sung_at === null ? 'ни разу' : formatMonthYear(song.last_sung_at)}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <label className="field">
          <span>Название</span>
          <input
            value={title}
            maxLength={200}
            onChange={(event) => {
              setTitle(event.target.value);
              // Typing over the title is choosing not to use the choir's song:
              // the number would otherwise carry a pill saying the whole choir
              // sings something nobody has under that name.
              setSongId(null);
            }}
          />
        </label>

        {kind === 'song' && songId === null && members.length > 0 && (
          <div className="field">
            <span>Кто поёт</span>
            <div className="chips">{members.map(singerChip)}</div>
          </div>
        )}

        {/* It warns and it does not stop: the app refuses nobody anything, and
            the whole of the two-rehearsal rule is the same sentence said in
            different places. The button stays live under it. */}
        {program?.barred === true && (
          <p className="notice notice--danger">
            У тебя две спевки подряд — по правилу на это участие ты не идёшь. Записаться можно, но
            скажи об этом регенту.
          </p>
        )}
      </Sheet>

      {/* Who sings one number. A sheet for the same reason the sign-up is one:
          it is filled in rather than looked at, and it is a wall of names. */}
      <Sheet
        open={naming !== null}
        label={naming === null ? '' : `Кто поёт · ${naming.title}`}
        onClose={() => setNaming(null)}
        foot={
          naming === null ? undefined : (
            <>
              {refusal(`item:${naming.item_id}`)}
              <button
                className="btn btn--primary"
                disabled={busy}
                onClick={() => {
                  const id = naming.item_id;
                  void act(() => api.setProgramSingers(id, singers), `item:${id}`).then((ok) => {
                    if (ok) setNaming(null);
                  });
                }}
              >
                Сохранить
              </button>
            </>
          )
        }
      >
        {naming !== null && (
          <>
            <h2 className="card-title">{naming.title}</h2>
            <div className="chips">{members.map(singerChip)}</div>
            <p className="hint">Имена встанут под названием номера — их видит весь хор.</p>
          </>
        )}
      </Sheet>

      {/* Taking a number out cannot be taken back by the same screen — what is
          gone is gone and has to be typed again — so it is asked from the sheet
          the other irreversible things in the app are asked from. */}
      <Ask
        open={doomed !== null}
        title={doomed === null ? '' : `Убрать «${doomed.title}»?`}
        cost={<>Номер пропадёт из программы. Вернуть его можно только записав заново.</>}
        verb="Убрать номер"
        busy={busy}
        failure={
          doomed !== null && failure !== null && failure.at === `item:${doomed.item_id}`
            ? failure.text
            : null
        }
        onConfirm={() => {
          if (doomed === null) return;
          const id = doomed.item_id;
          void act(() => api.removeProgramItem(id), `item:${id}`).then((ok) => {
            if (!ok) return;
            setRemoving(null);
            setMenuFor(null);
          });
        }}
        onClose={() => setRemoving(null)}
      />
    </>
  );
}
